import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';

interface QuizQuestion {
  id: number;
  question: string;
  choices: string[];
  correctIndex: number;
  explanation: string;
}

const QUIZ_MODE_KEY = 'mir.didYouKnowQuizMode';
const DISMISSED_UNTIL_KEY = 'mir.didYouKnowDismissedUntil';
const DAY_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'mir-did-you-know',
  templateUrl: './did-you-know.component.html',
  styleUrls: ['./did-you-know.component.css'],
  standalone: false,
})
export class DidYouKnowComponent implements OnInit {
  visible = false;
  quizMode = false;
  menuOpen = false;
  selectedIndex: number | null = null;
  answered = false;

  current: QuizQuestion | null = null;

  private questions: QuizQuestion[] = [];

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.isDismissed()) {
      return;
    }
    this.quizMode = localStorage.getItem(QUIZ_MODE_KEY) === 'true';
    this.http
      .get<QuizQuestion[]>('/assets/data/quiz-questions.json')
      .subscribe({
        next: questions => {
          this.questions = questions || [];
          this.visible = this.questions.length > 0;
          if (this.visible) {
            this.pickRandomQuestion();
          }
        },
        error: () => {
          this.visible = false;
        },
      });
  }

  anotherFact(): void {
    this.pickRandomQuestion();
  }

  toggleQuizMode(): void {
    this.quizMode = !this.quizMode;
    localStorage.setItem(QUIZ_MODE_KEY, String(this.quizMode));
    this.selectedIndex = null;
    this.answered = false;
  }

  selectAnswer(index: number): void {
    if (this.answered) {
      return;
    }
    this.selectedIndex = index;
    this.answered = true;
  }

  nextQuestion(): void {
    this.pickRandomQuestion();
  }

  openDismissMenu(): void {
    this.menuOpen = true;
  }

  closeDismissMenu(): void {
    this.menuOpen = false;
  }

  dismissForDays(days: number): void {
    const expiry = new Date(Date.now() + days * DAY_MS).toISOString();
    localStorage.setItem(DISMISSED_UNTIL_KEY, expiry);
    this.visible = false;
    this.menuOpen = false;
  }

  dismissForever(): void {
    localStorage.setItem(DISMISSED_UNTIL_KEY, 'forever');
    this.visible = false;
    this.menuOpen = false;
  }

  private isDismissed(): boolean {
    const raw = localStorage.getItem(DISMISSED_UNTIL_KEY);
    if (!raw) {
      return false;
    }
    if (raw === 'forever') {
      return true;
    }
    const expiry = Date.parse(raw);
    return !Number.isNaN(expiry) && Date.now() < expiry;
  }

  private pickRandomQuestion(): void {
    if (this.questions.length === 0) {
      return;
    }
    let pool = this.questions;
    if (this.current && this.questions.length > 1) {
      pool = this.questions.filter(q => q.id !== this.current!.id);
    }
    this.current = pool[Math.floor(Math.random() * pool.length)];
    this.selectedIndex = null;
    this.answered = false;
  }
}
